/**
 * useCurrentYearStock — current-year monthly + quarterly In-vs-Out stock
 * buckets for the chart pair, scoped to a product OR a variant. Mirrors
 * useCurrentYearVariantPricingHistory; fetched from the reports worker
 * independently of the ledger paging below it.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import type { StockMonthlyBucket, StockQuarterlyBucket } from '../models/stock.model'
import { parseApiError } from '../utils/api/parseApiError'

export type { StockMonthlyBucket, StockQuarterlyBucket } from '../models/stock.model'

export interface UseCurrentYearStockReturn {
  readonly year: number | null
  readonly monthly: ReadonlyArray<StockMonthlyBucket>
  readonly quarterly: ReadonlyArray<StockQuarterlyBucket>
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
}

interface ApiOkBody {
  readonly success: true
  readonly data: {
    readonly year: number
    readonly monthly: StockMonthlyBucket[]
    readonly quarterly: StockQuarterlyBucket[]
  }
}

export function useCurrentYearStock(
  scope: 'product' | 'variant' | 'material',
  id: string | null,
  organisationId: string | null = null
): UseCurrentYearStockReturn {
  const { authedFetch } = useAuth()
  const [year, setYear] = useState<number | null>(null)
  const [monthly, setMonthly] = useState<StockMonthlyBucket[]>([])
  const [quarterly, setQuarterly] = useState<StockQuarterlyBucket[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const tickRef = useRef<number>(0)
  const [tick, setTick] = useState<number>(0)

  const reload = useCallback((): void => {
    tickRef.current += 1
    setTick(tickRef.current)
  }, [])

  useEffect(() => {
    if (id === null || id.trim() === '') {
      setYear(null)
      setMonthly([])
      setQuarterly([])
      setError(null)
      setIsLoading(false)
      return
    }
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        const base =
          scope === 'product'
            ? APIROUTES.WAREHOUSEPRODUCTSTOCKCURRENTYEAR
            : scope === 'variant'
              ? APIROUTES.WAREHOUSEVARIANTSTOCKCURRENTYEAR
              : APIROUTES.MANUFACTURINGRAWMATERIALSTOCKCURRENTYEAR
        const param =
          scope === 'product' ? 'productId'
            : scope === 'variant' ? 'variantId'
              : 'materialId'
        // Stock charts honour the org filter (migration 0115). Raw
        // material stock is master-only; the orgId is only forwarded
        // for product/variant scope.
        const orgQuery =
          (scope === 'product' || scope === 'variant') && organisationId !== null && organisationId !== ''
            ? `&organisation_id=${encodeURIComponent(organisationId)}`
            : ''
        const res = await authedFetch(`${base}?${param}=${encodeURIComponent(id)}${orgQuery}`, { signal: ac.signal })
        if (!res.ok) {
          setError((await parseApiError(res)).message)
          setMonthly([])
          setQuarterly([])
          setYear(null)
          return
        }
        const body = (await res.json()) as ApiOkBody
        setYear(body.data.year)
        setMonthly(body.data.monthly)
        setQuarterly(body.data.quarterly)
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (!ac.signal.aborted) setIsLoading(false)
      }
    })()
    return (): void => ac.abort()
  }, [authedFetch, scope, id, organisationId, tick])

  return { year, monthly, quarterly, isLoading, error, reload }
}
